/**
 * Storage adapter for legacy token format compatibility
 *
 * Maps api-client keys to existing localStorage format:
 * - `${prefix}_accessToken` → `jwt-tokens[current-user]`
 * - `${prefix}_refreshToken` → `jwt-refresh-tokens[current-user]`
 *
 * In impersonation tabs, tokens live in sessionStorage under `imp-tokens`.
 * This adapter keeps api-client aligned with the legacy `$api` token reader.
 */

import { clearImpTokens, isImpersonating, readImpTokens, saveImpTokens } from '@/auth/tabStorage';

type Storage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
};

const parseJson = <T>(raw: string | null, fallback: T): T => {
  if (!raw) {
    return fallback;
  }
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

export const createLegacyTokenStorage = (keyPrefix: string): Storage => {
  const keys = {
    access: `${keyPrefix}_accessToken`,
    refresh: `${keyPrefix}_refreshToken`,
  };

  const storageMap: Record<string, string> = {
    [keys.access]: 'jwt-tokens',
    [keys.refresh]: 'jwt-refresh-tokens',
  };

  const getCurrentUser = () => localStorage.getItem('current-user');

  const getToken = (storageKey: string): string | null => {
    const impTokens = readImpTokens();
    if (impTokens) {
      return storageKey === 'jwt-tokens' ? impTokens.accessToken : impTokens.refreshToken;
    }

    const user = getCurrentUser();
    if (!user) {
      return null;
    }
    const tokens = parseJson<Record<string, string>>(localStorage.getItem(storageKey), {});
    return tokens[user] ?? null;
  };

  const setToken = (storageKey: string, value: string): void => {
    if (isImpersonating()) {
      const existing = readImpTokens();
      saveImpTokens({
        accessToken: storageKey === 'jwt-tokens' ? value : (existing?.accessToken ?? ''),
        refreshToken: storageKey === 'jwt-refresh-tokens' ? value : (existing?.refreshToken ?? ''),
      });
      return;
    }

    const user = getCurrentUser();
    if (!user) {
      return;
    }
    const tokens = parseJson<Record<string, string>>(localStorage.getItem(storageKey), {});
    tokens[user] = value;
    localStorage.setItem(storageKey, JSON.stringify(tokens));
  };

  const removeToken = (storageKey: string): void => {
    if (isImpersonating()) {
      clearImpTokens();
      return;
    }

    const user = getCurrentUser();
    if (!user) {
      return;
    }
    const tokens = parseJson<Record<string, string>>(localStorage.getItem(storageKey), {});
    delete tokens[user];
    localStorage.setItem(storageKey, JSON.stringify(tokens));
  };

  return {
    getItem: (key) => {
      const mapped = storageMap[key];
      return mapped ? getToken(mapped) : localStorage.getItem(key);
    },
    setItem: (key, value) => {
      const mapped = storageMap[key];
      if (mapped) {
        setToken(mapped, value);
      } else {
        localStorage.setItem(key, value);
      }
    },
    removeItem: (key) => {
      const mapped = storageMap[key];
      if (mapped) {
        removeToken(mapped);
      } else {
        localStorage.removeItem(key);
      }
    },
  };
};
